/**
 * API client centralizado para o módulo de rotinas agendadas auditadas.
 *
 * Tipos compartilhados + funções de API tipadas para evitar
 * duplicação de interfaces e endpoints inline nos componentes.
 */

import { api } from './client'

// ─── Tipos ─────────────────────────────────────────────────────────────────

export type RoutineBackend = 'sql' | 'shell'
export type RoutineApprovalStatus = 'pending' | 'approved' | 'rejected'
export type ExecutionStatus =
  | 'running'
  | 'success'
  | 'failed'
  | 'skipped_freeze'

export interface RoutineTargetSql {
  db_identifier: string
  credential_id: string
}

export interface RoutineTargetShell {
  instance_id: string
  region: string
}

export interface RoutinePayloadSql {
  sql: string
}

export interface RoutinePayloadShell {
  script: string
}

export interface Routine {
  id: number
  tenant_id: number
  name: string
  description: string | null
  backend: RoutineBackend
  target_ref: RoutineTargetSql | RoutineTargetShell
  payload: RoutinePayloadSql | RoutinePayloadShell
  schedule_expr: string
  timezone: string
  enabled: boolean
  approval_status: RoutineApprovalStatus
  eb_schedule_name: string | null
  freeze_respect: boolean
  created_by: number | null
  approved_by: number | null
  created_at: string
  updated_at: string
}

export interface RoutineCreateBody {
  name: string
  description?: string
  backend: RoutineBackend
  target_ref: RoutineTargetSql | RoutineTargetShell
  payload: RoutinePayloadSql | RoutinePayloadShell
  schedule_expr: string
  timezone: string
  freeze_respect: boolean
}

export interface RoutineExecution {
  id: number
  routine_id: number
  tenant_id: number
  correlation_id: string | null
  trigger: string
  status: ExecutionStatus
  started_at: string
  finished_at: string | null
  exit_code: number | null
  output_truncated: string | null
  error: string | null
}

// ─── Helpers ────────────────────────────────────────────────────────────────

// Barra final obrigatória: o backend registra a coleção como
// `/api/v1/tenants/{id}/routines/`. Sem a barra o FastAPI responde 307 e o
// axios reenvia o POST sem body em alguns navegadores → cria rotina vazia.
const collection = (tenantId: number) => `/v1/tenants/${tenantId}/routines/`
const item = (tenantId: number, id: number) =>
  `/v1/tenants/${tenantId}/routines/${id}`

// ─── API Functions ───────────────────────────────────────────────────────────

export const routinesApi = {
  /**
   * Cria uma nova rotina. `approval_status` inicial sempre 'pending'.
   * Requer permissão `routines:manage`.
   */
  createRoutine: (tenantId: number, body: RoutineCreateBody) =>
    api.post<Routine>(collection(tenantId), body),

  /** Lista todas as rotinas do tenant (lista direta). */
  listRoutines: (tenantId: number) =>
    api.get<Routine[]>(collection(tenantId)),

  /** Aprova uma rotina pendente (requer aprovador diferente do criador). */
  approveRoutine: (tenantId: number, id: number) =>
    api.post<Routine>(`${item(tenantId, id)}/approve`),

  /** Rejeita uma rotina pendente. */
  rejectRoutine: (tenantId: number, id: number) =>
    api.post<Routine>(`${item(tenantId, id)}/reject`),

  /** Desabilita uma rotina ativa. */
  disableRoutine: (tenantId: number, id: number) =>
    api.post<Routine>(`${item(tenantId, id)}/disable`),

  /** Lista execuções de uma rotina (lista direta). */
  listExecutions: (tenantId: number, id: number) =>
    api.get<RoutineExecution[]>(`${item(tenantId, id)}/executions`),
}
